import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BanknotesIcon, 
  ReceiptPercentIcon, 
  DocumentTextIcon, 
  CalendarDaysIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const BillingSettings = ({ settings, onSettingsChange, onHasChanges }) => {
  const [billingSettings, setBillingSettings] = useState({
    taxRate: 19,
    invoicePrefix: 'INV',
    paymentTerms: 30,
    lateFee: 0
  });

  useEffect(() => {
    if (settings?.billing) {
      setBillingSettings(settings.billing);
    }
  }, [settings]);

  const handleChange = (field, value) => {
    const newBillingSettings = {
      ...billingSettings,
      [field]: value
    };
    setBillingSettings(newBillingSettings);
    onSettingsChange({ ...settings, billing: newBillingSettings });
    onHasChanges(true);
  };

  const calculateTaxAmount = (amount) => {
    return (amount * billingSettings.taxRate / 100).toFixed(2);
  };

  const calculateTotal = (amount) => {
    return (amount + parseFloat(calculateTaxAmount(amount))).toFixed(2);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <BanknotesIcon className="h-6 w-6 text-green-400" />
        <div>
          <h3 className="text-lg font-semibold text-white">Paramètres de facturation</h3>
          <p className="text-white/60 text-sm">Configuration des taxes, factures et conditions de paiement</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Taux de TVA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-4"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <ReceiptPercentIcon className="h-5 w-5 text-blue-400" />
              <h4 className="font-semibold text-white">Taux de TVA</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={billingSettings.taxRate}
                  onChange={(e) => handleChange('taxRate', parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-20 bg-white/10 backdrop-blur-lg rounded-xl px-3 py-2 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                />
                <span className="text-white/80">%</span>
              </div>
              
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-white/60 text-sm mb-2">Exemple de calcul :</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/80">Montant HT :</span>
                    <span className="text-white">1000,00 DA</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">TVA ({billingSettings.taxRate}%) :</span>
                    <span className="text-white">{calculateTaxAmount(1000)} DA</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t border-white/20 pt-1">
                    <span className="text-white">Total TTC :</span>
                    <span className="text-white">{calculateTotal(1000)} DA</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Préfixe des factures */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <DocumentTextIcon className="h-5 w-5 text-purple-400" />
              <h4 className="font-semibold text-white">Préfixe des factures</h4>
            </div>
            
            <div className="space-y-3">
              <input
                type="text"
                value={billingSettings.invoicePrefix}
                onChange={(e) => handleChange('invoicePrefix', e.target.value)}
                maxLength="10"
                className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white placeholder-white/40 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="INV"
              />
              
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-white/60 text-sm mb-2">Aperçu des numéros de facture :</p>
                <div className="space-y-1 text-sm">
                  <div className="text-white/80">{billingSettings.invoicePrefix}-2024-001</div>
                  <div className="text-white/80">{billingSettings.invoicePrefix}-2024-002</div>
                  <div className="text-white/80">{billingSettings.invoicePrefix}-2024-003</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Conditions de paiement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <CalendarDaysIcon className="h-5 w-5 text-orange-400" />
              <h4 className="font-semibold text-white">Conditions de paiement</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={billingSettings.paymentTerms}
                  onChange={(e) => handleChange('paymentTerms', parseInt(e.target.value) || 0)}
                  min="0"
                  max="365"
                  className="w-20 bg-white/10 backdrop-blur-lg rounded-xl px-3 py-2 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                />
                <span className="text-white/80">jours</span>
              </div>
              
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-white/60 text-sm mb-2">Conditions courantes :</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <button
                    onClick={() => handleChange('paymentTerms', 0)}
                    className={`p-2 rounded-lg transition-colors ${
                      billingSettings.paymentTerms === 0 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'bg-white/5 text-white/80 hover:bg-white/10'
                    }`}
                  >
                    Comptant
                  </button>
                  <button
                    onClick={() => handleChange('paymentTerms', 15)}
                    className={`p-2 rounded-lg transition-colors ${
                      billingSettings.paymentTerms === 15 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'bg-white/5 text-white/80 hover:bg-white/10'
                    }`}
                  >
                    15 jours
                  </button>
                  <button
                    onClick={() => handleChange('paymentTerms', 30)}
                    className={`p-2 rounded-lg transition-colors ${
                      billingSettings.paymentTerms === 30 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'bg-white/5 text-white/80 hover:bg-white/10'
                    }`}
                  >
                    30 jours
                  </button>
                  <button
                    onClick={() => handleChange('paymentTerms', 60)}
                    className={`p-2 rounded-lg transition-colors ${
                      billingSettings.paymentTerms === 60 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'bg-white/5 text-white/80 hover:bg-white/10'
                    }`}
                  >
                    60 jours
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Frais de retard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-4"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <h4 className="font-semibold text-white">Frais de retard</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={billingSettings.lateFee}
                  onChange={(e) => handleChange('lateFee', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.1"
                  className="w-24 bg-white/10 backdrop-blur-lg rounded-xl px-3 py-2 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                />
                <span className="text-white/80">DA par jour</span>
              </div>
              
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-white/60 text-sm mb-2">Exemple de calcul :</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/80">Retard de 10 jours :</span>
                    <span className="text-white">{(billingSettings.lateFee * 10).toFixed(2)} DA</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">Retard de 30 jours :</span>
                    <span className="text-white">{(billingSettings.lateFee * 30).toFixed(2)} DA</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Résumé des paramètres */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10"
      >
        <h3 className="text-lg font-semibold text-white mb-4">Résumé des paramètres de facturation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <ReceiptPercentIcon className="h-6 w-6 text-blue-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{billingSettings.taxRate}%</div>
            <div className="text-white/60 text-sm">Taux de TVA</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <DocumentTextIcon className="h-6 w-6 text-purple-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{billingSettings.invoicePrefix}</div>
            <div className="text-white/60 text-sm">Préfixe facture</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <CalendarDaysIcon className="h-6 w-6 text-orange-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{billingSettings.paymentTerms}</div>
            <div className="text-white/60 text-sm">Jours de paiement</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{billingSettings.lateFee}</div>
            <div className="text-white/60 text-sm">Frais/jour (DA)</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BillingSettings; 